import React, { useCallback, useState } from 'react';
import {
    DmsTextField,
    useGet,
    fetchData,
    HTTP_STATUS,
    intl,
    DmsButton
} from 'dmsbasereact';
import { Box, Typography, Grid, Alert, CircularProgress } from '@mui/material';
import { SettingsViewEdit } from '../../components/SettingsLayouts';

/**
 * CFM View Content - displays CFM API settings as read-only
 */
const CfmViewContent = ({ data }) => {
    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                    {intl.get('BaseUrl').d('Base URL')}
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    {data.cfm_base_url || '-'}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                    {intl.get('Status').d('Status')}
                </Typography>
                <Typography variant="body1">
                    {data.enabled
                        ? intl.get('Configured').d('Configured')
                        : intl.get('NotConfigured').d('Not Configured')}
                </Typography>
            </Grid>
        </Grid>
    );
};

/**
 * CFM Edit Content - displays CFM API settings as editable form fields
 */
const CfmEditContent = ({ formData, onFieldChange, onTestConnection, testing, testResult, isConfigured }) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                    <DmsTextField
                        name="cfm_base_url"
                        label={intl.get('BaseUrl').d('Base URL')}
                        fullWidth
                        value={formData.cfm_base_url ?? ''}
                        onChange={(e) => onFieldChange('cfm_base_url', e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <DmsTextField
                        name="api_key"
                        label={intl.get('ApiKey').d('API Key')}
                        type="password"
                        fullWidth
                        onChange={(e) => onFieldChange('api_key', e.target.value)}
                        placeholder={isConfigured ? intl.get('LeaveBlankToKeep').d('Leave blank to keep existing') : ''}
                    />
                </Grid>
            </Grid>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <DmsButton
                    variant="secondary"
                    text={testing ? intl.get('Testing').d('Testing...') : intl.get('TestConnection').d('Test Connection')}
                    onClick={onTestConnection}
                    disabled={testing || !isConfigured}
                />
                {testing && <CircularProgress size={20} />}
            </Box>

            {testResult && (
                <Alert severity={testResult.connection_ok ? 'success' : 'error'}>
                    {testResult.message}
                </Alert>
            )}

            {!isConfigured && (
                <Alert severity="info">
                    {intl.get('CfmNotConfigured').d('Save the settings first to test the connection.')}
                </Alert>
            )}
        </Box>
    );
};

/**
 * CfmApiSettings - CFM API configuration for CRM validation.
 * Uses SettingsViewEdit pattern with view/edit mode.
 */
export const CfmApiSettings = () => {
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting] = useState(false);

    // CFM data fetching
    const { data: cfmData, isLoading: cfmLoading, refetch: refetchCfm } = useGet('/cfm_api_settings', null, true);

    const prepareCfmFormData = useCallback((data) => {
        return {
            cfm_base_url: data.cfm_base_url || 'https://ws.cfm.org.br:8080',
            api_key: '',
            enabled: data.enabled ?? false,
        };
    }, []);

    const buildCfmPayload = useCallback((formData) => {
        const payload = {
            cfm_base_url: formData.cfm_base_url,
        };
        if (formData.api_key) {
            payload.api_key = formData.api_key;
        }
        return payload;
    }, []);

    const handleCfmSave = useCallback(async (payload) => {
        try {
            // Require api_key for new configurations
            if (!payload.api_key && !cfmData?.data?.enabled) {
                return null;
            }
            const response = await fetchData('/cfm_api_settings', 'POST', true, payload, false, true);
            if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.CREATED_201) {
                setTestResult(null);
                return response.data?.data || response.data;
            }
            return null;
        } catch (error) {
            console.error('Failed to save CFM API settings:', error);
            return null;
        }
    }, [cfmData]);

    const handleTestConnection = useCallback(async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const response = await fetchData('/cfm_api_settings/test', 'POST', true);
            if (response.status === HTTP_STATUS.OK_200) {
                setTestResult(response.data?.data || response.data);
            } else {
                setTestResult({ connection_ok: false, message: response.data?.detail || 'Test failed' });
            }
        } catch (error) {
            setTestResult({ connection_ok: false, message: error.message });
        } finally {
            setTesting(false);
        }
    }, []);

    const cfmSettings = cfmData?.data ?? {
        cfm_base_url: 'https://ws.cfm.org.br:8080',
        api_key: '',
        enabled: false
    };

    // Custom edit content with test connection
    const EditContentWithTest = useCallback(({ formData, onFieldChange }) => (
        <CfmEditContent
            formData={formData}
            onFieldChange={onFieldChange}
            onTestConnection={handleTestConnection}
            testing={testing}
            testResult={testResult}
            isConfigured={cfmSettings.enabled}
        />
    ), [handleTestConnection, testing, testResult, cfmSettings.enabled]);

    return (
        <Box>
            {/* CFM API Section */}
            <SettingsViewEdit
                data={cfmSettings}
                loading={cfmLoading}
                title={intl.get('CfmApiSettings').d('CFM API Settings')}
                description={intl.get('CfmApiDescription').d(
                    'Configure CFM (Conselho Federal de Medicina) API for doctor CRM validation during registration.'
                )}
                viewContent={CfmViewContent}
                editContent={EditContentWithTest}
                prepareFormData={prepareCfmFormData}
                buildPayload={buildCfmPayload}
                onSave={handleCfmSave}
                onDataUpdated={refetchCfm}
                confirmMessage={intl.get('ConfirmSaveCfmMessage').d(
                    'Are you sure you want to save the CFM API settings?'
                )}
            />

            {/* Documentation Link */}
            <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                    {intl.get('CfmDocumentation').d('Documentation')}: {' '}
                    <a
                        href="https://sistemas.cfm.org.br/listamedicos/arquivos/manualwebservices.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        CFM Web Services Manual
                    </a>
                </Typography>
            </Box>
        </Box>
    );
};
